import React, { useEffect, useState, useRef } from 'react';
import { Modal } from '@/components/Modal';
import { useSourceUploadModalState } from './hooks/useSourceUploadModalState';
import { SourceUploadModalContent } from './components/SourceUploadModalContent';
import {
    prepareFilesForQueue,
    getSubmitButtonText,
} from '@/utils/upload-source';
import { useSourceCreationStore } from '@/stores/source-creation.store';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface FileSourceModalProps {
    isOpen: boolean;
    targetFolderId?: string;
    reportId?: string;
    onClose: () => void;
}

export function FileSourceModal({
    isOpen,
    targetFolderId,
    reportId,
    onClose,
}: FileSourceModalProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [currentPlaybackTime, setCurrentPlaybackTime] = useState<number>(0);
    const setTargetFolderId = useSourceCreationStore(
        (state) => state.setTargetFolderId,
    );
    const setTargetReportId = useSourceCreationStore(
        (state) => state.setTargetReportId,
    );

    const state = useSourceUploadModalState(isOpen);

    useEffect(() => {
        if (isOpen) {
            setTargetFolderId(targetFolderId ?? null);
            setTargetReportId(reportId ?? null);
        }
    }, [
        isOpen,
        targetFolderId,
        reportId,
        setTargetFolderId,
        setTargetReportId,
    ]);

    const handleClose = () => {
        setTargetFolderId(null);
        setTargetReportId(null);
        onClose();
    };

    useEffect(() => {
        setCurrentPlaybackTime(0);
    }, [state.activeFileId]);

    const handleUploadMore = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const selectedFiles = Array.from(e.target.files || []);
        await state.handleFilesAdded(selectedFiles);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUpload = () => {
        if (state.files.length === 0) return;

        const folderId = useSourceCreationStore.getState().targetFolderId;
        const addItems = useSourceCreationStore.getState().addFileItems;
        addItems(prepareFilesForQueue(state.files), folderId);

        handleClose();
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
            />
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title={t('addModal.addVideoFileTitle')}
                contentTestId="add-video-file-modal"
                closeButtonTestId="add-video-file-modal-close"
                onCancel={state.files.length > 0 ? handleClose : undefined}
                onSubmit={state.files.length > 0 ? handleUpload : undefined}
                submitText={
                    state.files.length > 0
                        ? getSubmitButtonText(state.files.length)
                        : undefined
                }
                secondaryButtonText={
                    state.files.length > 0
                        ? t('addModal.uploadMore')
                        : undefined
                }
                onSecondarySubmit={
                    state.files.length > 0 ? handleUploadMore : undefined
                }
                size={state.isMultipleMode ? 'large' : 'medium'}
                fullHeight
            >
                <SourceUploadModalContent
                    state={state}
                    currentPlaybackTime={currentPlaybackTime}
                    onTimeUpdate={setCurrentPlaybackTime}
                />
            </Modal>
        </>
    );
}
